/** Federal-style timekeeping types and helpers for the demo. */

export type LaborType = "PT" | "ST" | "AT" | "LWOP";

export const LABOR_TYPE_HELP: Record<
  LaborType,
  { label: string; meaning: string; leaveImpact: string }
> = {
  PT: {
    label: "Paid Time-Off",
    meaning:
      "Paid leave bin. Codes underneath include Accrued PT, Bank PT (M&A), Holiday, Sick, and similar.",
    leaveImpact: "Decrements the matching leave balance.",
  },
  ST: {
    label: "Standard Time",
    meaning:
      "Working time — direct/billable or indirect/non-billable project and overhead charge codes. Includes unpaid/uncompensated overtime codes under Total Time Accounting.",
    leaveImpact: "Does not reduce leave balances.",
  },
  AT: {
    label: "Administrative Time",
    meaning: "Non-leave admin time such as training, all-hands, and company meetings.",
    leaveImpact: "Does not reduce leave balances.",
  },
  LWOP: {
    label: "Leave Without Pay",
    meaning: "Unpaid leave charge codes.",
    leaveImpact: "Does not draw paid leave balances.",
  },
};

export type TimesheetStatus =
  | "Draft"
  | "Submitted"
  | "Approved"
  | "Rejected"
  | "Correction requested";

export type PeriodCadence = "weekly" | "biweekly";
export type HourBasis = "40_per_week" | "80_per_biweekly";

/** HR employment status driving timesheet hour-floor proration (DEC-TIME-002). */
export type EmploymentStatus = "FT" | "PT20" | "PT32" | "PTV";

export const EMPLOYMENT_STATUS_HELP: Record<
  EmploymentStatus,
  { label: string; weeklyHours: number | null; meaning: string }
> = {
  FT: {
    label: "Full Time",
    weeklyHours: 40,
    meaning: "100% of the company full-time hour basis for the period.",
  },
  PT20: {
    label: "Half Time (PT20)",
    weeklyHours: 20,
    meaning: "50% of the company full-time hour basis (20 hrs/week equivalent).",
  },
  PT32: {
    label: "Part Time 32",
    weeklyHours: 32,
    meaning: "80% of the company full-time hour basis (32 hrs/week equivalent).",
  },
  PTV: {
    label: "Part Time Variable",
    weeklyHours: null,
    meaning: "No period minimum; paid for hours worked. Empty timesheets still blocked.",
  },
};

export const EMPLOYMENT_STATUSES: EmploymentStatus[] = ["FT", "PT20", "PT32", "PTV"];

/** Current employee certification text version (DEC-TIME-003). */
export const CERTIFICATION_TEXT_VERSION = "v1-tta-2026-08";

export const CERTIFICATION_TEXT =
  "I certify that the hours entered are true, accurate, and complete; that I charged only authorized codes for work performed; and that this timesheet includes all hours worked, paid or unpaid (Total Time Accounting).";

export const SUPERVISOR_ATTESTATION_TEXT_VERSION = "v1-knowledge-2026-08";

export const SUPERVISOR_ATTESTATION_TEXT =
  "I attest that I have knowledge the work was performed and that hours are charged to appropriate cost objectives.";

/** Charge-code catalog class (DEC-CHARGE-001). */
export type ChargeCodeClass =
  | "leave"
  | "bp"
  | "ga"
  | "other_indirect"
  | "direct";

export type ChargeCodeLifecycle =
  | "draft"
  | "submitted"
  | "finance_review"
  | "pm_confirm"
  | "open"
  | "closed"
  | "rejected";

export type ChargeCode = {
  code: string;
  title: string;
  labor_type: LaborType;
  manager_name: string;
  manager_employee_id: string;
  /** open|closed for charging; lifecycle covers create factories. */
  status: "open" | "closed";
  lifecycle?: ChargeCodeLifecycle;
  class?: ChargeCodeClass;
  company_id: string;
  /** Empty = none authorized yet; include employee id for access. */
  authorized_employee_ids: string[];
  /** Work-authorization POP (inclusive ISO dates). */
  pop_start?: string;
  pop_end?: string;
  /** Project/contract description shown in picker. */
  description?: string;
  /** Unpaid/uncompensated OT code for TTA. */
  is_unpaid_ot?: boolean;
  /** Optional company label (e.g. HT for holiday PT). */
  alias?: string;
  award_value?: number;
  ceiling_value?: number;
  funded_value?: number;
};

export type CompanyTimekeepingPolicy = {
  id: string;
  company_id: string;
  company_name: string;
  cadence: PeriodCadence;
  hour_basis: HourBasis;
  hour_increment: number;
  effective_date: string;
  end_date: string | null;
  /** DCAA daily contemporaneous entry (DEC-TIME-004). */
  require_daily_entry?: boolean;
  /** Days into the past that may be edited without a late-entry reason. */
  late_entry_window_days?: number;
  total_time_accounting_required?: boolean;
  unpaid_ot_charge_code?: string;
  certification_text_version?: string;
};

export type TimesheetHeader = {
  id: string;
  employee_id: string;
  employee: string;
  company_id: string;
  period_start: string;
  period_end: string;
  status: TimesheetStatus;
  due_at: string;
  submitted_at?: string;
  certification_at?: string;
  certified_by?: string;
  certification_text_version?: string;
  /** When supervisor approved or rejected. */
  decided_at?: string;
  supervisor_attested_at?: string;
  supervisor_attested_by?: string;
  supervisor_attestation_text_version?: string;
  correction_reason?: string;
  reject_reason?: string;
  /** Policy id effective as of period_start (hour checks). */
  policy_id?: string;
  /** Audited proxy sheet for prolonged leave (DEC-TIME-005). */
  is_proxy?: boolean;
  proxy_reason?: "prolonged_leave";
  proxy_prepared_by?: string;
};

export type TimesheetLine = {
  id: string;
  timesheet_id: string;
  charge_code: string;
  labor_type: LaborType;
  /** ISO date → hours */
  hours: Record<string, number>;
};

export type TimesheetEntryNote = {
  id: string;
  timesheet_id: string;
  line_id: string;
  date: string;
  old_hours: number;
  new_hours: number;
  note: string;
  at: string;
  by: string;
  /** Late/backdate reason when outside daily entry window. */
  reason_code?: string;
  concurrence_at?: string;
};

export type ChargeCodeAccessRequest = {
  id: string;
  code: string;
  owner: string;
  reason: string;
  status: "Submitted" | "Approved" | "Rejected";
  submitted_at: string;
  reviewed_at?: string;
  review_note?: string;
};

export type EmployeeBurdenAssignment = {
  id: string;
  employee_id: string;
  structure_name: string;
  effective_date: string;
  end_date: string | null;
};

export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function formatIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(iso: string, n: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + n);
  return formatIsoDate(d);
}

export function eachDay(start: string, end: string): string[] {
  const out: string[] = [];
  let cur = start;
  while (cur <= end) {
    out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

export function formatPeriodLabel(start: string, end: string): string {
  const a = parseIsoDate(start);
  const b = parseIsoDate(end);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const year = b.getFullYear();
  return `${a.toLocaleDateString(undefined, opts)} – ${b.toLocaleDateString(undefined, opts)}, ${year}`;
}

export function weekdayShort(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, { weekday: "short" });
}

export function policyForDate(
  policies: CompanyTimekeepingPolicy[],
  companyId: string,
  asOf: string,
): CompanyTimekeepingPolicy | undefined {
  return policies
    .filter(
      (p) =>
        p.company_id === companyId &&
        p.effective_date <= asOf &&
        (p.end_date == null || p.end_date >= asOf),
    )
    .sort((a, b) => b.effective_date.localeCompare(a.effective_date))[0];
}

export function expectedHoursForPeriod(
  policy: CompanyTimekeepingPolicy,
  ftePercent: number,
): number {
  const basis = policy.hour_basis === "40_per_week" ? 40 : 80;
  if (policy.cadence === "weekly" && policy.hour_basis === "80_per_biweekly") {
    return 40 * ftePercent;
  }
  if (policy.cadence === "biweekly" && policy.hour_basis === "40_per_week") {
    return 80 * ftePercent;
  }
  return basis * ftePercent;
}

/** FTE factor for status; null means no minimum (PTV). */
export function fteFactorForStatus(status: EmploymentStatus): number | null {
  switch (status) {
    case "FT":
      return 1;
    case "PT20":
      return 0.5;
    case "PT32":
      return 0.8;
    case "PTV":
      return null;
  }
}

/**
 * Required minimum hours for the period given employment status.
 * null = no minimum (PTV). There is never an upper bound on submit.
 */
export function requiredHoursForPeriod(
  policy: CompanyTimekeepingPolicy,
  status: EmploymentStatus,
): number | null {
  const factor = fteFactorForStatus(status);
  if (factor == null) return null;
  return expectedHoursForPeriod(policy, factor);
}

export type HourFloorResult = "ok" | "short" | "empty";

/** Submit gate: at-or-above floor (or any positive total for PTV). */
export function hourFloorCheck(
  total: number,
  required: number | null,
): HourFloorResult {
  if (total <= 0.001) return "empty";
  if (required == null) return "ok";
  if (total + 0.001 < required) return "short";
  return "ok";
}

/** True when there is a minimum and total exceeds it (flag for approver). */
export function isOverFloor(total: number, required: number | null): boolean {
  if (required == null) return false;
  return total > required + 0.001;
}

export function lineTotal(line: TimesheetLine): number {
  return Object.values(line.hours).reduce((s, h) => s + (Number(h) || 0), 0);
}

export function sheetTotal(lines: TimesheetLine[]): number {
  return lines.reduce((s, l) => s + lineTotal(l), 0);
}

export function dayTotal(lines: TimesheetLine[], date: string): number {
  return lines.reduce((s, l) => s + (Number(l.hours[date]) || 0), 0);
}

export function hoursByLaborType(
  lines: TimesheetLine[],
  type: LaborType,
): number {
  return lines
    .filter((l) => l.labor_type === type)
    .reduce((s, l) => s + lineTotal(l), 0);
}

/** Round to nearest 0.25; return null if not on a quarter-hour. */
export function normalizeQuarterHour(raw: number): number | null {
  if (!Number.isFinite(raw) || raw < 0) return null;
  const rounded = Math.round(raw * 4) / 4;
  if (Math.abs(raw - rounded) > 0.001) return null;
  return rounded;
}

export function isEditableStatus(status: TimesheetStatus): boolean {
  return status === "Draft" || status === "Rejected" || status === "Correction requested";
}

export function canRequestCorrection(status: TimesheetStatus): boolean {
  return status === "Submitted" || status === "Approved";
}

export function carryForwardLines(
  priorLines: TimesheetLine[],
  newTimesheetId: string,
): TimesheetLine[] {
  return priorLines
    .filter((l) => lineTotal(l) > 0)
    .map((l, i) => ({
      id: `${newTimesheetId}-L${i + 1}`,
      timesheet_id: newTimesheetId,
      charge_code: l.charge_code,
      labor_type: l.labor_type,
      hours: {},
    }));
}

export function employeeHasCodeAccess(
  code: ChargeCode,
  employeeId: string,
): boolean {
  return code.authorized_employee_ids.includes(employeeId);
}

/** Active work authorization: open, authorized, within POP for asOf date. */
export function workAuthorizationOk(
  code: ChargeCode,
  employeeId: string,
  asOf: string,
): { ok: boolean; reason?: string } {
  if (code.status !== "open") return { ok: false, reason: "Charge code is closed" };
  if (!employeeHasCodeAccess(code, employeeId)) {
    return { ok: false, reason: "Not authorized for this charge code" };
  }
  if (code.pop_start && asOf < code.pop_start) {
    return { ok: false, reason: "Before period of performance" };
  }
  if (code.pop_end && asOf > code.pop_end) {
    return { ok: false, reason: "After period of performance" };
  }
  return { ok: true };
}

/** True when editing a day older than the late-entry window (needs reason). */
export function needsLateEntryReason(
  policy: CompanyTimekeepingPolicy | undefined,
  dayIso: string,
  todayIso: string,
): boolean {
  if (!policy?.require_daily_entry) return false;
  const windowDays = policy.late_entry_window_days ?? 1;
  const cutoff = addDays(todayIso, -windowDays);
  return dayIso < cutoff;
}

export function todayIso(): string {
  return formatIsoDate(new Date());
}
